/**
 * The static adapter: the same client surface, with no platform behind it.
 *
 * It answers from a snapshot exported out of the running platform, and it holds
 * what you do in this browser in localStorage. Nothing is installed, nothing
 * listens on a port, and the whole thing is files on a static host.
 *
 * Read this before relying on it.
 *
 * **Nothing here is enforced.** In the real platform three tiers of
 * authorisation run in the database on every request, the consent gateway
 * decides every disclosure, and audit rows are written by triggers that no code
 * path can skip. A snapshot is a file. A file in a browser can be read and
 * edited by whoever has the browser, and this adapter cannot stop them, because
 * there is no second party to appeal to. What you see is a recording of what
 * the platform decided, not a platform deciding.
 *
 * That recording is worth having and it is why this is a snapshot rather than a
 * set of fixtures: the refusals are real refusals, the redaction is real
 * redaction, the row scopes were applied in SQL before the rows were written to
 * these files, and the figures came from the engines. Hand-written fixtures
 * would show whatever the person writing them believed the platform did.
 *
 * **Writes stay in this browser.** An action records a local change against the
 * surface it affected and the screen reflects it. Nothing reaches a database,
 * nothing reaches another viewer, and clearing site data undoes all of it.
 */
import type {
  AccessPayload, AuditPayload, Bootstrap, ExplainPayload, ListPayload,
  PagePayload, Row,
} from './types'
import { ApiError } from './client'

// Relative to the document rather than to a configured base: this folder may
// be served from the root of a host, from a subdirectory, or from a share, and
// the one thing always true is that snapshot/ sits beside index.html.
const SNAPSHOT = 'snapshot'
const KEY_ROLE = 'fp.static.role'
const KEY_LOCALE = 'fp.static.locale'
const KEY_WRITES = 'fp.static.writes'

export interface SnapshotIndex {
  generated_at: string
  source: string
  pages: number
  explainers: number
  audits: number
  roles: {
    role_code: string
    role_name: string
    role_name_ar: string | null
    purpose: string
    category_code: string
    category_name: string
    category_name_ar: string | null
    display_name: string
    job_title: string | null
    partner_code: string | null
    partner_name: string | null
    landing: string
    surfaces: number
  }[]
  templates: Record<string, number>
}

interface StoredPage { status: number; payload: PagePayload }
type PageSet = Record<string, StoredPage>

export interface LocalWrite {
  at: string
  path: string
  fields: Record<string, string>
  role: string
  summary: string
}

/** localStorage, wrapped. It throws in a private window and comes back empty
 *  after site data is cleared, and neither should break the page. */
const store = {
  get<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key)
      return raw === null ? fallback : JSON.parse(raw) as T
    } catch { return fallback }
  },
  set(key: string, value: unknown) {
    try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* ignore */ }
  },
  remove(key: string) {
    try { localStorage.removeItem(key) } catch { /* ignore */ }
  },
}

const cache = new Map<string, unknown>()

async function load<T>(path: string): Promise<T> {
  if (cache.has(path)) return cache.get(path) as T
  const url = new URL(`${SNAPSHOT}/${path}`, document.baseURI)
  const response = await fetch(url, { cache: 'force-cache' })
  if (!response.ok) {
    throw new ApiError(response.status === 404 ? 404 : 500,
      response.status === 404 ? 'Not in the snapshot' : 'The snapshot could not be read',
      response.status === 404
        ? `This build carries no recording of ${path}. The export walks what each `
          + `role could reach when it ran; a surface added since is not in it.`
        : `Could not read ${path} from the snapshot. If this build was opened as `
          + `a file, serve the folder over HTTP instead: a browser refuses to `
          + `fetch local files from a page.`)
  }
  const body = await response.json() as T
  cache.set(path, body)
  return body
}

export const snapshot = {
  index: () => load<SnapshotIndex>('index.json'),
  clearCache: () => cache.clear(),
}

export function currentRole(): string | null {
  return store.get<string | null>(KEY_ROLE, null)
}

export function writes(): LocalWrite[] {
  return store.get<LocalWrite[]>(KEY_WRITES, [])
}

export function clearLocal() {
  store.remove(KEY_WRITES)
  store.remove(KEY_ROLE)
  store.remove(KEY_LOCALE)
  cache.clear()
}

function requireRole(): string {
  const role = currentRole()
  if (!role) throw new ApiError(401, 'Not signed in', 'Choose a role to begin.')
  return role
}

// ---------------------------------------------------------------------------
// Local writes, applied over the snapshot
// ---------------------------------------------------------------------------
/**
 * How an action changes what is on screen.
 *
 * Each rule patches the surface the action affected, so a decision recorded on
 * an application shows on that application. The patches are deliberately
 * shallow: this is a demonstration of the flow, not a reimplementation of the
 * engines. A decision here runs no eligibility evaluation, writes no outbox
 * event, and asks no four-eyes constraint, because those live in the database
 * that is not here.
 */
const RULES: {
  match: RegExp
  summary: (fields: Record<string, string>, id: string) => string
  apply?: (payload: PagePayload, fields: Record<string, string>) => void
}[] = [
  {
    match: /^\/app\/applications\/([0-9a-f-]+)\/decide$/,
    summary: (f) => `Application decided: ${f.decision || 'recorded'}`,
    apply: (payload, f) => {
      const a = payload.data.a as Record<string, unknown> | undefined
      if (!a) return
      a.decision = f.decision
      a.status = f.decision === 'approved' ? 'offered'
        : f.decision === 'declined' ? 'declined' : 'info_requested'
      a.decision_at = new Date().toISOString()
      if (f.decline_reason_code) a.decline_reason_code = f.decline_reason_code
      if (f.note) a.decline_reason_text = f.note
    },
  },
  {
    match: /^\/app\/tasks\/([0-9a-f-]+)\/claim$/,
    summary: () => 'Task claimed',
  },
  {
    match: /^\/app\/tasks\/([0-9a-f-]+)\/release$/,
    summary: () => 'Task released',
  },
  {
    match: /^\/app\/config\/set$/,
    summary: (f) => `Setting ${f.setting_key} set to ${f.value}`,
    apply: (payload, f) => {
      const d = payload.data.d as Record<string, unknown> | undefined
      if (!d || d.setting_key !== f.setting_key) return
      d.effective_value = f.value
      d.source = 'platform'
      d.platform_reason = f.reason || null
      d.platform_set_at = new Date().toISOString()
    },
  },
  {
    match: /^\/app\/config\/set-partner$/,
    summary: (f) => `${f.partner_code} override on ${f.setting_key}`,
  },
  {
    match: /^\/app\/config\/clear$/,
    summary: (f) => `${f.partner_code} override cleared on ${f.setting_key}`,
  },
  { match: /^\/app\/requests\/create$/, summary: () => 'Funding request raised' },
  {
    match: /^\/app\/requests\/([0-9a-f-]+)\/route$/,
    summary: () => 'Request routed to providers',
  },
  { match: /^\/app\/consents\/create$/, summary: () => 'Consent granted' },
  {
    match: /^\/app\/consents\/([0-9a-f-]+)\/revoke$/,
    summary: () => 'Consent revoked',
  },
  { match: /^\/app\/offers\/([0-9a-f-]+)\/accept$/, summary: () => 'Offer accepted' },
  { match: /^\/app\/reason$/, summary: (f) => `Reason stated: ${f.reason}` },
]

function applyWrites(path: string, payload: PagePayload) {
  for (const write of writes()) {
    for (const rule of RULES) {
      if (!rule.apply) continue
      if (!rule.match.test(write.path)) continue
      // A rule patches only the surface the action named: an application id in
      // the action path has to be the record being viewed.
      const id = write.path.match(/[0-9a-f-]{36}/)?.[0]
      if (id && !path.includes(id)) continue
      rule.apply(payload, write.fields)
    }
  }
}

// ---------------------------------------------------------------------------
// Lists, filtered and paged in the browser
// ---------------------------------------------------------------------------
/**
 * The snapshot holds each list's first page as the server rendered it, with
 * every row the role could see up to the server's own page size. Filtering and
 * paging here work on those rows, with the same semantics the server applies:
 * a text filter is a case-insensitive substring, a boolean filter splits on
 * truthiness, everything else is equality on the raw value.
 *
 * The total is recomputed from what was filtered rather than kept from the
 * snapshot, because a count that did not move when a filter was applied is the
 * kind of wrong that nobody notices until it is quoted in a meeting.
 */
function refine(list: ListPayload, params: Record<string, string>): ListPayload {
  let rows: Row[] = list.rows
  const applied: Record<string, string> = {}

  for (const filter of list.filters) {
    const value = (params[`f_${filter.name}`] || '').trim()
    if (!value) continue
    applied[filter.name] = value
    rows = rows.filter((row) => {
      const raw = row.raw[filter.name]
      if (filter.kind === 'ilike') {
        return String(raw ?? '').toLowerCase().includes(value.toLowerCase())
      }
      if (filter.kind === 'bool') {
        return value === 'true' ? Boolean(raw) : !raw
      }
      return String(raw ?? '') === value
    })
  }

  const sort = params.sort || ''
  if (sort) {
    const field = sort.replace(/^-/, '')
    const direction = sort.startsWith('-') ? -1 : 1
    const index = list.columns.findIndex((c) => c.field === field)
    if (index >= 0) {
      rows = [...rows].sort((a, b) => {
        const x = a.raw[field]
        const y = b.raw[field]
        if (x === y) return 0
        if (x === null || x === undefined) return 1
        if (y === null || y === undefined) return -1
        if (typeof x === 'number' && typeof y === 'number') {
          return (x - y) * direction
        }
        return String(x).localeCompare(String(y)) * direction
      })
    }
  }

  const size = Math.max(5, Math.min(200, Number(params.size) || list.size || 25))
  const total = rows.length
  const pages = Math.max(1, Math.ceil(total / size))
  const page = Math.min(Math.max(1, Number(params.page) || 1), pages)
  const slice = rows.slice((page - 1) * size, page * size)

  return {
    ...list,
    rows: slice,
    total,
    page,
    pages,
    size,
    applied,
    filters: list.filters.map((f) => ({ ...f, value: applied[f.name] || '' })),
  }
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

// ---------------------------------------------------------------------------
// The adapter
// ---------------------------------------------------------------------------
async function pageSet(): Promise<PageSet> {
  return load<PageSet>(`pages/${requireRole()}.json`)
}

export const staticApi = {
  access: async (): Promise<AccessPayload & { static_roles: SnapshotIndex }> => {
    const [access, index] = await Promise.all([
      load<AccessPayload>('access.json'), snapshot.index(),
    ])
    return { ...access, demo_accounts: [], demo_password: null,
             static_roles: index }
  },

  signIn: async (fields: { role_code: string }) => {
    const index = await snapshot.index()
    const role = index.roles.find((r) => r.role_code === fields.role_code)
    if (!role) {
      throw new ApiError(404, 'Role not in this build',
        `${fields.role_code} was not exported into this snapshot.`)
    }
    store.set(KEY_ROLE, role.role_code)
    cache.delete(`pages/${role.role_code}.json`)
    return { ok: true, landing: role.landing }
  },

  signOut: async () => {
    store.remove(KEY_ROLE)
    return { ok: true }
  },

  switchRole: async (role_code: string) => staticApi.signIn({ role_code }),

  setLocale: async (code: string) => {
    store.set(KEY_LOCALE, code)
    return { ok: true, locale: code }
  },

  bootstrap: async (): Promise<Bootstrap> => {
    const boot = clone(await load<Bootstrap>(`bootstrap/${requireRole()}.json`))
    const chosen = store.get<string | null>(KEY_LOCALE, null)
    if (chosen && chosen !== boot.locale.locale_code) {
      const other = boot.locales.find((l) => l.locale_code === chosen)
      if (other) boot.locale = other
    }
    return boot
  },

  page: async (path: string, params?: Record<string, string>): Promise<PagePayload> => {
    const pages = await pageSet()
    const found = pages[path] || pages[path.replace(/\/$/, '')]
    if (!found) {
      throw new ApiError(404, 'Not in this build',
        `No recording of ${path} for this role. The snapshot holds what each `
        + `role could reach when it was exported.`)
    }
    const payload = clone(found.payload)
    applyWrites(path, payload)
    if (payload.template === 'list.html' && params) {
      const list = payload.data.d as ListPayload
      payload.data.d = refine(list, params)
    }
    if (found.status >= 400) {
      throw new ApiError(found.status,
        found.status === 403 ? 'Not permitted' : 'A stated reason is required',
        (payload.data?.detail as string) || '', payload)
    }
    return payload
  },

  list: async (sectionKey: string, params?: Record<string, string>) => {
    const pages = await pageSet()
    for (const entry of Object.values(pages)) {
      const payload = entry.payload
      if (payload.template !== 'list.html') continue
      const list = payload.data.d as ListPayload
      if (list.section.section_key !== sectionKey) continue
      return { template: 'list.html',
               data: { d: refine(clone(list), params || {}) } }
    }
    throw new ApiError(404, 'Not in this build',
      `No recording of the ${sectionKey} list for this role.`)
  },

  explain: async (sectionKey: string): Promise<ExplainPayload> => {
    try {
      return await load<ExplainPayload>(
        `explain/${sectionKey.replace(/\//g, '~')}.json`)
    } catch {
      return { missing: true, section_key: sectionKey,
               detail: 'No explainer for this section is in the snapshot.' }
    }
  },

  audit: async (link: string): Promise<AuditPayload> => {
    const tail = link.replace(/^\/app\/audit\//, '').replace(/\//g, '~')
    try {
      return await load<AuditPayload>(`audit/${tail}.json`)
    } catch {
      throw new ApiError(404, 'Trail not in this build',
        'The export captured a sample of trails rather than all of them, so '
        + 'this record has none here. In the running platform every record has '
        + 'one, written by a database trigger.')
    }
  },

  enumValues: async (schema: string, name: string) => ({
    type: `${schema}.${name}`, values: [] as string[],
  }),

  health: async () => {
    const index = await snapshot.index()
    return { roles: index.roles.length, pages: index.pages,
             explainers: index.explainers, audits: index.audits }
  },

  /**
   * An action, recorded in this browser.
   *
   * It returns the destination the live handler would have returned, so the
   * screens navigate the same way. What it does not do is anything the live
   * handler does: no grant check, no constraint, no audit row, no event. The
   * banner on every page says so, because an action that appears to succeed is
   * the one place a demonstration can mislead somebody into thinking a control
   * exists.
   */
  act: async (path: string, fields: Record<string, string | number | boolean | null | undefined>) => {
    const clean: Record<string, string> = {}
    for (const [key, value] of Object.entries(fields)) {
      if (value !== null && value !== undefined) clean[key] = String(value)
    }
    const rule = RULES.find((r) => r.match.test(path))
    const id = path.match(/[0-9a-f-]{36}/)?.[0] || ''
    const entry: LocalWrite = {
      at: new Date().toISOString(),
      path,
      fields: clean,
      role: currentRole() || '',
      summary: rule ? rule.summary(clean, id) : `Action: ${path}`,
    }
    store.set(KEY_WRITES, [entry, ...writes()].slice(0, 200))

    // Where the live handler would have sent the browser.
    if (/\/app\/applications\/[0-9a-f-]+\/decide/.test(path)) {
      return { ok: true, redirect: `/app/applications/${id}` }
    }
    if (/\/app\/config\/(set|set-partner|clear)/.test(path)) {
      return { ok: true, redirect: `/app/config/${clean.setting_key}` }
    }
    if (/\/app\/requests\/[0-9a-f-]+\/route/.test(path)) {
      return { ok: true, redirect: `/app/requests/${id}` }
    }
    if (path === '/app/reason') {
      return { ok: true, redirect: clean.target || '/app' }
    }
    if (/\/app\/tasks\//.test(path)) return { ok: true, redirect: '/app/inbox' }
    return { ok: true, redirect: '/app' }
  },
}
